import React, { useState } from 'react';
import { Facebook, Instagram, Youtube, Mail, MapPin, Calendar, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TikTokIcon } from './TikTokIcon';
import VisionStudiosBanner from './VisionStudiosBanner';
import { submitForm } from '../lib/formSubmissions';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const result = await submitForm({
        form_type: 'newsletter',
        submitter_email: email,
        fields: { email },
      });

      if (!result.success) throw new Error(result.error);

      setStatus('success');
      setMessage('Thank you for subscribing to our newsletter!');
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };

  const getGoogleMapsUrl = (address: string) => {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  };

  return (
    <footer className="relative bg-navy-950 text-white overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary-900/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-3xl" />
      </div>

      <VisionStudiosBanner />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Newsletter Section */}
        <div id="newsletter" className="py-16 border-b border-white/10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-600 to-blue-600 mb-6 shadow-lg shadow-primary-900/30">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-3xl font-bold mb-3 text-white">
              Subscribe to Our Newsletter
            </h3>
            <p className="text-gray-400 mb-8 text-lg leading-relaxed">
              Stay updated with our latest news, events, and spiritual insights.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <div className="flex-1">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-5 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                  placeholder="Enter your email"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={status === 'loading'}
                className="bg-primary-600 text-white px-6 py-3.5 rounded-xl hover:bg-primary-500 transition-all duration-200 disabled:opacity-50 flex items-center justify-center font-semibold hover:shadow-lg hover:shadow-primary-600/20 active:scale-[0.98] whitespace-nowrap"
              >
                {status === 'loading' ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    Subscribe
                    <Send className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>
            </form>
            {message && (
              <p className={`mt-4 text-sm ${status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {message}
              </p>
            )}
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img
                src="https://hcamiahiytrjczzsrodb.supabase.co/storage/v1/object/public/etwcministry//ETWC%20Logo%20White.PNG"
                alt="ETWC Ministry Logo"
                className="h-14 w-auto"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Empowering lives through faith, community, and service. A full Gospel ministry based in Cleveland, Ohio.
            </p>
            <div className="flex space-x-3">
              {[
                { href: "https://www.facebook.com/profile.php?id=100064727584791", icon: <Facebook size={18} />, label: "Facebook" },
                { href: "https://www.instagram.com/etwcministry", icon: <Instagram size={18} />, label: "Instagram" },
                { href: "https://www.youtube.com/@clevelandstreetpreachers", icon: <Youtube size={18} />, label: "YouTube" },
                { href: "https://www.tiktok.com/@clevelandstreetpreachers", icon: <div className="w-[18px] h-[18px]"><TikTokIcon /></div>, label: "TikTok" },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-200"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/statement-of-faith', label: 'Statement of Faith' },
                { to: '/services', label: 'Services' },
                { to: '/connect-groups', label: 'Connect Groups' },
                { to: '/news', label: 'News' },
                { to: '/give', label: 'Give' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-400 hover:text-white transition-colors text-[15px]">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-5">Resources</h4>
            <ul className="space-y-3">
              {[
                { to: '/live-stream', label: 'Live Stream' },
                { to: '/sermon-archive', label: 'Sermon Archive' },
                { to: '/prayer-request', label: 'Prayer Requests' },
                { to: '/testimonials', label: 'Testimonials' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-400 hover:text-white transition-colors text-[15px]">
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://www.youtube.com/@clevelandstreetpreachers"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors text-[15px]"
                >
                  YouTube Channel
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-5">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="text-primary-400 mt-0.5 flex-shrink-0" size={16} />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Church</p>
                  <a
                    href={getGoogleMapsUrl('6486 W 130th Street, Cleveland, OH 44130')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-white transition-colors text-sm leading-relaxed"
                  >
                    6486 W 130th Street<br />Cleveland, OH 44130
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="text-primary-400 mt-0.5 flex-shrink-0" size={16} />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Mailing</p>
                  <span className="text-gray-300 text-sm leading-relaxed">
                    PO BOX 1172<br />Elyria, OH 44036
                  </span>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="text-primary-400 flex-shrink-0" size={16} />
                <a
                  href="mailto:etwcministry@outlook.com"
                  className="text-gray-300 hover:text-white transition-colors text-sm"
                >
                  etwcministry@outlook.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Calendar className="text-primary-400 flex-shrink-0" size={16} />
                <span className="text-gray-300 text-sm">Sunday Service: 1:30 PM EST</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/10 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Evangelize the World Christian Ministry. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm">
            Website Designed by{' '}
            <a
              href="https://BOLDStudios.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 hover:from-blue-300 hover:to-cyan-300 transition-all font-bold"
            >
              BOLD Studios
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
